import { DtResourceCache } from './loader/DtResourceCache';
import { DT_LOADER_IDLE_EVENT } from './DtEventTypes';
import { LightPresets } from '../lmv-compat/DtLightPresets';

import { av, avp } from "@adsk/lmv";

// We cannot simply inherit from LMV's implementation class here,
// because LMV will already create an instance of that internally.
// So we just implement our custom API as a stand-alone class and
// use a proxy to forward accesses to the correct instance.

// TODO: It would be great if we wouldn't have to replace most instances of 'this' with 'this.#viewer3dImpl'.
// TODO-LMV: Last synced with Viewer3dImpl: 04/15/26
class DtViewer3DImpl {
  #viewer3dImpl; // The Viewer3DImpl instance from LMV
  #geomCache;

  constructor(viewer3dImpl) {
    this.#viewer3dImpl = viewer3dImpl;

    // This must go before initialize. It only affects WebGPU.
    // We use this to disable streaming draw for overlays, which can cause major performance issues if they contain
    // many fragments (e.g. section meshes in large models).
    // Note that this disables streaming draw completely, so it uploads all geometries to the GPU and therefore
    // bypasses the OOCTM memory budget (for geometries, not sure how it affects uniforms).
    avp.disableStreamingDraw();

    this.#replaceMethods();
  }

  // ----- LMV class overrides -----
  // Note that we can only override methods that are called "from the outside",
  // i.e. from the app or inside the Tandem viewer repo.
  // Methods that are called by LMV internally need to be replaced, see #replaceMethods below.

  initialize(initOptions) {
    initOptions = initOptions || {};
    // We need to request 3 id targets in case the WebGL renderer is used.
    // This is required for category highlighting to work.
    // For WebGPU, the API is different and category highlighting is enabled later (see #configureRenderer).
    initOptions.numIdTargets = 3;

    this.#viewer3dImpl.initialize(initOptions);

    // LMV sets rcs.signalProgressByRendering to true after the first model (viewer.model) is done loading
    // (model.isLoadDone()). This doesn't make sense for Tandem, because we might load additional data
    // afterwards (when starting with a partial view), or just load multiple models in general. So we restore
    // our old behavior by checking the geomCache for overall loading activity.
    const that = this;
    Object.defineProperty(this.#viewer3dImpl._rcs, 'signalProgressByRendering', {
      get: function () {
        return !that.#geomCache || that.#geomCache.isIdle();
      },
      set: function () {/* ignore set attempts by LMV */}
    });

    this.#configureRenderer();
  }

  geomCache() {
    if (!this.#geomCache) {
      this.#geomCache = new DtResourceCache(this.#viewer3dImpl.api._sessionId);
      this.#geomCache.addViewer(this.#viewer3dImpl.api);
    }

    return this.#geomCache;
  }

  addModel(model, preserveTools) {
    this.#viewer3dImpl.addModel(model, preserveTools);
    model.selector.setDefaultSelectionType(av.SelectionType.OVERLAYED);
  }

  addMainPassOverlay(scene, drawBefore) {
    if (scene.addedAsMainPassOverlay) {
      return;
    }

    this.#viewer3dImpl.addMainPassOverlay(scene, drawBefore);
    scene.addedAsMainPassOverlay = true;
  }

  removeMainPassOverlay(scene) {
    if (scene.addedAsMainPassOverlay) {
      this.#viewer3dImpl.removeMainPassOverlay(scene);
      scene.addedAsMainPassOverlay = false;
    }
  }

  // ----- LMV class overrides end -----
  // ----- Tandem added -----

  #replaceMethods() {
    // We cannot simply override some methods of LMV's viewerImpl class,
    // if they get called internally in LMV (because it uses the native impl instance, not this proxy).
    // So we need to replace these methods here.


    const origAnimLoop = this.#viewer3dImpl._animLoop.bind(this.#viewer3dImpl);
    this.#viewer3dImpl._animLoop = (moreWorkToDoInExecuteTasks) => {
      // We invalidate canvas boundaries once per frame, because insights dashboards embed the viewer in a
      // scrollable container that doesn't cause the cached value to be invalidated. Doing this once per frame
      // still allows to use the cached value for multiple requests within a single frame.
      this.#viewer3dImpl.canvasBoundingclientRectDirty = true;

      return origAnimLoop(moreWorkToDoInExecuteTasks);
    };
  }

  #configureRenderer() {
    // Apply Tandem-specific renderer configurations.
    if (this.#viewer3dImpl.renderer()) {
      // Disabling depth writes for transparent (main-scene) elements, to avoid occlusion of effects like the
      // room grid pattern on heatmaps through windows / transparent objects.
      this.#viewer3dImpl.matman().toggleDepthWriteTransparent(false);

      // Enable dithered ghosting (basically Tandem's ghosting effect ported to LMV).
      this.#viewer3dImpl.renderer().setDitheredGhosting(true);

      // Hovering the canvas should clear category highlighting.
      // Category highlighting is enabled for WebGPU below. For WebGL, it is enabled via the numIdTargets option
      // in initialize above.
      this.#viewer3dImpl.renderer().setClearCategoryOnRollover(true);

      if (this.#viewer3dImpl.isWebGPU()) {
        // Enabling category highlighting reallocates some render targets, so we do it in an animation frame
        // callback to avoid destroying them while they're used.
        requestAnimationFrame(() => {
          this.#viewer3dImpl.renderer().setCategoryHighlightingEnabled(true);
        });

        // This sets the transparency threshold for writing to the viewDepth and normal buffers
        // used in the SSAO pass. Objects with a transparency value higher than this are ignored.
        this.#viewer3dImpl.glrenderer().setNdBufferOpacityThreshold(0.749);
      }
    }
  }

  isDarkBackground() {
    const preset = LightPresets[this.#viewer3dImpl.currentLightPreset()];
    return !preset.darkerFade;
  }

  waitForRendererIdle() {
    if (!this.#viewer3dImpl._workThisTick) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let listener = (e) => {
        //console.log(e);
        if (e.value.finalFrame) {
          this.#viewer3dImpl.api.removeEventListener(av.FINAL_FRAME_RENDERED_CHANGED_EVENT, listener);
          resolve();
        }
      };

      this.#viewer3dImpl.api.addEventListener(av.FINAL_FRAME_RENDERED_CHANGED_EVENT, listener);
    });
  }

  async waitForLoaderIdle() {
    if (this.#geomCache?.isIdle()) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.#viewer3dImpl.api.addEventListener(DT_LOADER_IDLE_EVENT, resolve, { once: true });
    });
  }

  //Called when the geometry loader goes idle (is done loading whatever geometry was requested by any of the
  //multiple visible models)
  onGeomLoadComplete() {
    this.#viewer3dImpl._isLoading = false;

    // Fire the event so we know the geometry is done loading.
    this.#viewer3dImpl.api.dispatchEvent({
      type: DT_LOADER_IDLE_EVENT
    });
  }

  // Called for each single model that finishes loading geometries.
  onLoadComplete(model) {
    this.#viewer3dImpl.onLoadComplete(model);
    // This is set to false in LMV, but we only want to do that after all models are loaded,
    // so we do it in onGeomLoadComplete instead.
    this.#viewer3dImpl._isLoading = true;
  }

  // TODO-LMV: Not supported in LMV. Needed? If not, remove all instances.
  trackFrameBudget(value) {

    // this.#viewer3dImpl._getRcs().trackFrameBudget = value;
  }
  // ----- Tandem added end -----
}

export function makeDtViewer3DImplProxy(lmvViewer3dImpl) {
  const dtViewer3dImpl = new DtViewer3DImpl(lmvViewer3dImpl);

  const proxyHandler = {
    // Target is the dtViewer3dImpl instance, receiver will usually be the proxy (=== this).
    get(target, prop, receiver) {
      // Check if the value exists on the dtViewer3dImpl instance.
      // If not, set the target to the lmvViewer3dImpl instance.
      let value = target[prop];
      if (value === undefined) {
        target = lmvViewer3dImpl;
        value = target[prop];
      }

      // Make sure functions get called with the correct this context.
      if (value instanceof Function) {
        return function (...args) {
          return value.apply(this === receiver ? target : this, args);
        };
      }

      // Simply return non-function properties.
      return value;
    },

    set(target, prop, value) {
      // Set all properties on the base class instance.
      lmvViewer3dImpl[prop] = value;
      return true;
    }
  };

  const proxy = new Proxy(dtViewer3dImpl, proxyHandler);
  return proxy;
}